import React from "react";
import { Radio, Select, Input, Alert, Spin, Tag } from "antd";
import { DatePickerItem } from "../../../../../Components/Items";
import { fechaTxt } from "../aguinaldoUtils";

const ETIQUETA_ESTATUS = { borrador: "Borrador", calculada: "Calculada" };

/**
 * Paso 2 — dónde se paga el aguinaldo.
 *  · Ordinaria: dentro de la prenómina abierta de cada tipo de periodo (una prenómina sólo cubre
 *    a los empleados de su tipo, así que hay un selector por tipo).
 *  · Extraordinaria: un periodo extraordinario sólo con el aguinaldo y su ISR. El periodo y su
 *    prenómina se crean al APLICAR, no antes.
 */
const PasoAplicacion = ({
  ejercicio, tipoAplicacion, destinos, extraordinaria, gruposDestino, sinTipo, cargandoDestinos,
  soloLectura, onCambio,
}) => {
  const prenominaDe = (tipoPeriodoId) =>
    (destinos || []).find((d) => d.tipoPeriodoId === tipoPeriodoId)?.prenominaId || null;

  const elegirPrenomina = (tipoPeriodoId, prenominaId) => {
    const otros = (destinos || []).filter((d) => d.tipoPeriodoId !== tipoPeriodoId);
    onCambio({ destinos: [...otros, { tipoPeriodoId, prenominaId: prenominaId || null }] });
  };

  return (
    <div className="aguinaldo-paso">
      <div className="aguinaldo-titulo">¿Dónde se paga el aguinaldo?</div>
      <Radio.Group
        value={tipoAplicacion}
        disabled={soloLectura}
        onChange={(e) => onCambio({ tipoAplicacion: e.target.value })}
        className="aguinaldo-opciones"
      >
        <Radio value="ordinaria" className="aguinaldo-opcion">
          <b>En una prenómina ordinaria</b>
          <div className="aguinaldo-tenue">
            Se agrega al recibo de la nómina del periodo, junto con el sueldo.
          </div>
        </Radio>
        <Radio value="extraordinaria" className="aguinaldo-opcion">
          <b>En un periodo extraordinario</b>
          <div className="aguinaldo-tenue">
            Un recibo aparte sólo con el aguinaldo y su ISR. Se crea al aplicar.
          </div>
        </Radio>
      </Radio.Group>

      {tipoAplicacion === "ordinaria" && (
        <Spin spinning={!!cargandoDestinos}>
          <div className="aguinaldo-subtitulo">Prenómina por tipo de periodo</div>
          <div className="aguinaldo-tenue aguinaldo-parrafo">
            Sólo aparecen las prenóminas ordinarias abiertas (borrador o calculada). Si la del periodo
            todavía no existe, créala en PreNómina y vuelve a este paso.
          </div>
          {(gruposDestino || []).length === 0 && !cargandoDestinos && (
            <Alert type="info" showIcon message="No hay empleados elegibles con tipo de periodo." />
          )}
          <div className="aguinaldo-rejilla">
            {(gruposDestino || []).map((g) => (
              <React.Fragment key={g.tipoPeriodoId}>
                <label className="aguinaldo-etiqueta">
                  {g.nombre}
                  <div className="aguinaldo-tenue">{g.empleados} empleado(s)</div>
                </label>
                <div>
                  {g.prenominas.length ? (
                    <Select
                      allowClear
                      value={prenominaDe(g.tipoPeriodoId)}
                      disabled={soloLectura}
                      placeholder="Elige la prenómina"
                      style={{ width: "100%", maxWidth: 560 }}
                      onChange={(v) => elegirPrenomina(g.tipoPeriodoId, v)}
                      options={g.prenominas.map((p) => ({
                        value: p._id,
                        label: (
                          <span>
                            {p.nombre}{" "}
                            <span className="aguinaldo-tenue">
                              ({fechaTxt(p.fechaInicio)} – {fechaTxt(p.fechaFin)})
                            </span>{" "}
                            <Tag color={p.estatus === "calculada" ? "blue" : "default"}>
                              {ETIQUETA_ESTATUS[p.estatus] || p.estatus}
                            </Tag>
                          </span>
                        ),
                      }))}
                    />
                  ) : (
                    <Alert
                      type="warning"
                      showIcon
                      style={{ maxWidth: 560 }}
                      message="No hay prenóminas abiertas de este tipo: sus empleados quedarán fuera."
                    />
                  )}
                </div>
              </React.Fragment>
            ))}
          </div>
          {sinTipo > 0 && (
            <Alert
              style={{ marginTop: 12 }}
              type="warning"
              showIcon
              message={`${sinTipo} empleado(s) no tienen tipo de periodo y no pueden recibir el aguinaldo en una prenómina ordinaria.`}
            />
          )}
        </Spin>
      )}

      {tipoAplicacion === "extraordinaria" && (
        <div className="aguinaldo-rejilla" style={{ marginTop: 12 }}>
          <label className="aguinaldo-etiqueta">Fecha de pago</label>
          <DatePickerItem
            value={extraordinaria.fechaPago}
            disabled={soloLectura}
            onChange={(d) => onCambio({ extraordinaria: { ...extraordinaria, fechaPago: d || null } })}
          />
          <label className="aguinaldo-etiqueta">Nombre (opcional)</label>
          <Input
            value={extraordinaria.nombre}
            disabled={soloLectura}
            maxLength={120}
            placeholder={`Aguinaldo ${ejercicio || ""}`}
            style={{ maxWidth: 420 }}
            onChange={(e) => onCambio({ extraordinaria: { ...extraordinaria, nombre: e.target.value } })}
          />
          <span />
          <div className="aguinaldo-tenue">
            Se creará el periodo "Extraordinario (Finiquito/Aguinaldo)" de un día con esta fecha y una
            prenómina que sólo calcula el aguinaldo y el I.S.R. Art. 174 (sin sueldo ni IMSS).
          </div>
        </div>
      )}
    </div>
  );
};

export default PasoAplicacion;
